import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Typography,
  Alert,
  Link,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { ko } from 'date-fns/locale';
import { Notification, NOTIFICATION_TARGET_TYPES, NOTIFICATION_STATUS, USER_COLLECTION, User, ENTERPRISE_COLLECTION, Enterprise, Board } from '../type';
import { CloseButton } from './CloseButton';
import { CancelButton } from './CancelButton';
import { useEffect, useState } from 'react';
import { collection, getFirestore, onSnapshot, orderBy, query, Timestamp } from '@firebase/firestore';
import { useAuth } from './AuthProvider';
import { userService } from '../service/firebase/userService';
import { UserSelection } from './UserSelection';
import { eventService } from '../service/firebase/eventService';
import { noticeService } from '../service/firebase/noticeService';
import { useQuery } from '@tanstack/react-query';
import { Event, Notice } from "../type";
import ContentSelectorDialog from './ContentSelectorDialog';
import { boardService } from '../service/firebase/boardService';

interface CreateNotificationDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (notification: Notification) => void;
}

export const CreateNotificationDialog = ({
  open,
  onClose,
  onSave,
}: CreateNotificationDialogProps) => {
  const { user } = useAuth();
  
    // 이벤트 목록 조회
  const { data: events = [] } = useQuery({
      queryKey: ['events'],
      queryFn: () => eventService.getAllEvents(),
  });

  // 공지사항 목록 조회
  const { data: notices = [] } = useQuery({
    queryKey: ['notices'],
    queryFn: () => noticeService.getAll(),
  });

  // 게시물 목록 조회
  const { data: boards = [] } = useQuery({
    queryKey: ['boards'],
    queryFn: () => boardService.getAll(),
  });

  const [notificationData, setNotificationData] = useState<Partial<Notification>>({
    title: '',
    body: '',
    targetType: NOTIFICATION_TARGET_TYPES.TOKEN_LIST,
    targetTokens: null,
    status: NOTIFICATION_STATUS.PENDING,
    scheduledAt: Timestamp.now(),
    sentAt: null,
    userId: user?.uid || '',
    targetUsers: null,
    targetEnterprises: null,
  });

  const [independentUsers, setIndependentUsers] = useState<User[]>([]);
  const [selectedIndependentUsers, setSelectedIndependentUsers] = useState<User[]>([]);
  const [enterpriseUsers, setEnterpriseUsers] = useState<User[]>([]);
  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);
  const [selectedEnterpriseUsers, setSelectedEnterpriseUsers] = useState<User[]>([]);
  const [selectorOpen, setSelectorOpen] = useState(false);

  useEffect(() => {
    const db = getFirestore();
    const usersRef = collection(db, USER_COLLECTION);
    const q = query(usersRef, orderBy('createdAt', 'desc'));

    onSnapshot(q, (snapshot) => {
      const i_users: User[] = [];
      const e_users: User[] = [];

      snapshot.forEach(doc => {
        const data = doc.data() as User;
        if (data.uid?.length > 0 && data.displayName?.length > 0) {
          if (data.isEnterpriseUser && data.enterpriseId) {
            e_users.push(data);
          } else {
            i_users.push(data);
          }
        }
      });

      setIndependentUsers(i_users);
      setEnterpriseUsers(e_users);
    });

    const enterprisesRef = collection(db, ENTERPRISE_COLLECTION);
    const eq = query(enterprisesRef, orderBy('createdAt', 'desc'));

    onSnapshot(eq, (snapshot) => {
      const list: Enterprise[] = [];

      snapshot.forEach(doc => {
        const data = doc.data() as Enterprise;
        list.push(data);
      });

      setEnterprises(list);
    });
  }, []);

  function isEnterpriseFullySelected(enterpriseId: string) {
    const remaining = enterpriseUsers.filter(user => user.enterpriseId === enterpriseId);
    return remaining.length === 0;
  }

  const isPresetTargetType = (targetType?: Notification['targetType']) => {
    return targetType === NOTIFICATION_TARGET_TYPES.ALL ||
      targetType === NOTIFICATION_TARGET_TYPES.APPROVED_OPTIONAL_USERS;
  };

  const getFCMTokens = (users: User[]): Promise<string[]> => {
    return new Promise(async (resolve, reject) => {
      const tokens = await userService.getTokensForUsers(users.map(u => u.uid));
      if (tokens.length > 0) {
        resolve(tokens);
      } else {
        reject(new Error('No valid FCM tokens found.'));
      }
    });
  };

  const handleCreate = async () => {
    const { title, body, targetType, scheduledAt } = notificationData;
    
    if (!title?.trim() || !body?.trim() || !targetType || !scheduledAt) {
      alert('제목, 내용, 대상 타입, 예약 시각을 모두 입력해주세요.');
      return;
    }

        // 간단한 HTML 생성
    const allowedEnterprises: string[] = [];
    const tmpEnterpriseUsers = isPresetTargetType(notificationData.targetType) ? [] : [...selectedEnterpriseUsers];
    for (let i = tmpEnterpriseUsers.length - 1; i >= 0; i--) {
      const user = tmpEnterpriseUsers[i];
      if (user.enterpriseId) {
        const fullySelected = isEnterpriseFullySelected(user.enterpriseId!);
        if (fullySelected) {
          // 해당 기업의 ID를 추가
          if (!allowedEnterprises.includes(user.enterpriseId)) {
            allowedEnterprises.push(user.enterpriseId);
          }
          tmpEnterpriseUsers.splice(i, 1); // 기업 사용자 목록에서 제거
        }
      } else {
        tmpEnterpriseUsers.splice(i, 1); // 기업 사용자 목록에서 제거
      }
    }
    const allowedUsers = isPresetTargetType(notificationData.targetType) ? [] : [...selectedIndependentUsers, ...tmpEnterpriseUsers].map(u => u.uid);

    let targetTokens = null;
    if (notificationData.targetType === NOTIFICATION_TARGET_TYPES.TOKEN_LIST) {
      const tokens = await getFCMTokens([...selectedIndependentUsers, ...selectedEnterpriseUsers]);
      targetTokens = tokens;
    }

    const notificationDoc: Notification = {
      notificationId: `notification_${Date.now()}`,
      title: title.trim(),
      body: body.trim(),
      content: notificationData.content ?? null,
      targetType,
      targetTokens: targetTokens || null,
      status: NOTIFICATION_STATUS.PENDING,
      scheduledAt,
      sentAt: null,
      failedAt: null,
      createdAt: Timestamp.now(),
      userId: user?.uid || '',
      targetUsers: allowedUsers,
      targetEnterprises: allowedEnterprises,
    };

    onSave(notificationDoc);
    
    handleClose();
  };

  const handleClose = () => {
    setNotificationData({
      title: '',
      body: '',
      targetType: NOTIFICATION_TARGET_TYPES.TOKEN_LIST,
      targetTokens: null,
      status: NOTIFICATION_STATUS.PENDING,
      scheduledAt: Timestamp.now(),
      sentAt: null,
      userId: user?.uid || '',
      targetUsers: null,
      targetEnterprises: null,
    });
    onClose();
  };

  const handleTargetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const targetType = e.target.checked ? NOTIFICATION_TARGET_TYPES.ALL : NOTIFICATION_TARGET_TYPES.TOKEN_LIST;
    setNotificationData({ ...notificationData, targetType });
  };

  const handleTargetTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNotificationData({
      ...notificationData,
      targetType: e.target.value as Notification['targetType'],
    });
  };

  const canSave = () => {
    let isInvalid = notificationData.title?.trim().length === 0 || notificationData.body?.trim().length === 0 || !notificationData.scheduledAt;
    isInvalid = isInvalid || (notificationData.targetType === NOTIFICATION_TARGET_TYPES.TOKEN_LIST && selectedIndependentUsers.length + selectedEnterpriseUsers.length === 0);
    return !isInvalid;
  }

  const handleSelectContent = () => {
    setSelectorOpen(true);
  };

  const handleContentChosen = (content: any) => {
    setNotificationData(prev => ({
      ...prev,
      body: `${prev.body || ''}`,
      content: content,
    }));
    setSelectorOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      {/* 상단 닫기 버튼 및 제목 */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 3, pb: 2 }}>
        <Typography variant="h6" sx={{ fontSize: '1.25rem' }}>
          새 알림 추가
        </Typography>
        <CloseButton onClose={handleClose} />
      </Box>
      <Divider />
      <DialogContent sx={{ display: 'flex', flexDirection: 'row', gap: 5, mt: 0.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
          <TextField
            label="제목"
            value={notificationData.title || ''}
            onChange={(e) =>
              setNotificationData({ ...notificationData, title: e.target.value })
            }
            required
          />
          <TextField
            label="내용"
            multiline
            rows={4}
            value={notificationData.body || ''}
            onChange={(e) =>
              setNotificationData({ ...notificationData, body: e.target.value })
            }
            required
          />

          <Box sx={{
            width: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            border: '1px solid',
            borderColor: 'grey.300',
            borderRadius: 2,
            p: 2, 
            mb: 2,
            position: 'relative',
          }}>
            <Typography
              variant="subtitle2"
              sx={{
                position: 'absolute',
                top: -12,
                left: 16,
                backgroundColor: 'background.paper',
                px: 1,
              }}
            >
              연결화면
            </Typography>
            <Button
              variant="outlined"
              onClick={handleSelectContent}
              fullWidth
              sx={{ mb: 1 }}
            >
              홈/이벤트/공지사항 선택
            </Button>

            {notificationData.content ? (
              <Box sx={{ width: '100%', mt: 1, textAlign: 'left' }}>
                <Typography variant="body2">선택된 연결화면:</Typography>
                <Typography variant="body2">- 타입: {notificationData.content?.type}</Typography>
                {notificationData.content?.id && (
                  <Typography variant="body2">- ID: {notificationData.content?.id}</Typography>
                )}
                {notificationData.content?.title && (
                  <Typography variant="body2">- 제목: {notificationData.content?.title}</Typography>
                )} 
                {notificationData.content?.content && (
                  <Typography variant="body2">
                    - 상세내용:{' '}
                    {notificationData.content?.type !== 'home' && notificationData.content?.type !== 'profile' && notificationData.content?.type !== 'board' ? (
                      <Link href={notificationData.content?.content} target="_blank" rel="noopener">
                        {notificationData.content?.content}
                      </Link>
                    ) : (
                      notificationData.content?.content
                    )}
                  </Typography>
                )}
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  sx={{ mt: 1 }}
                  onClick={() => setNotificationData((prev) => ({ ...prev, content: null }))}
                >
                  선택 취소
                </Button>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                선택된 연결화면이 없습니다.
              </Typography>
            )}
          </Box>

          <ContentSelectorDialog
            open={selectorOpen}
            onClose={() => setSelectorOpen(false)}
            onSelect={handleContentChosen}
            events={events}
            notices={notices}
            boards={boards}
          />

          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
            <DateTimePicker
              label="예약 발송 시각"
              value={notificationData.scheduledAt instanceof Timestamp ? notificationData.scheduledAt.toDate() : new Date()}
              onChange={(newValue) =>
                setNotificationData({
                  ...notificationData,
                  scheduledAt: newValue ? Timestamp.fromDate(newValue) : Timestamp.now(),
                })
              }
              minDateTime={new Date()}
            />
          </LocalizationProvider>

          <Alert severity="info">
            예약 시각은 현재 시각보다 이후로 설정해야 합니다.
          </Alert>
        </Box>

        <Box sx={{  flex: 1, display: 'flex', flexDirection: 'column', gap: 2, mt: -0.5 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend">발송 대상</FormLabel>
              <RadioGroup
                value={notificationData.targetType}
                onChange={handleTargetTypeChange}
              >
                <FormControlLabel
                  value={NOTIFICATION_TARGET_TYPES.TOKEN_LIST}
                  control={<Radio />}
                  label="선택 사용자"
                />
                <FormControlLabel
                  value={NOTIFICATION_TARGET_TYPES.ALL}
                  control={<Radio />}
                  label="전체 사용자"
                />
                <FormControlLabel
                  value={NOTIFICATION_TARGET_TYPES.APPROVED_OPTIONAL_USERS}
                  control={<Radio />}
                  label="승인 + 선택약관 동의 사용자"
                />
              </RadioGroup>
            </FormControl>

            {notificationData.targetType === NOTIFICATION_TARGET_TYPES.TOKEN_LIST ? (
            <UserSelection 
              title="선택 사용자" 
              isChecked={false} 
              checkBoxClick={handleTargetChange}
              showTargetToggle={false}
              selectedEnterpriseUsers={selectedEnterpriseUsers}
              setSelectedEnterpriseUsers={setSelectedEnterpriseUsers}
              selectedIndependentUsers={selectedIndependentUsers}
              setSelectedIndependentUsers={setSelectedIndependentUsers}
              enterpriseUsers={enterpriseUsers}
              setEnterpriseUsers={setEnterpriseUsers}
              independentUsers={independentUsers}
              setIndependentUsers={setIndependentUsers}
              enterprises={enterprises}
            />
            ) : notificationData.targetType === NOTIFICATION_TARGET_TYPES.APPROVED_OPTIONAL_USERS ? (
              <Alert severity="info">
                개인/기업 구분 없이 승인 상태이고 선택약관에 동의한 사용자에게만 발송됩니다.
              </Alert>
            ) : (
              <Alert severity="warning">
                기존 전체 사용자 발송입니다. 선택약관 동의 여부와 무관하게 전체 토픽으로 발송됩니다.
              </Alert>
            )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        <CancelButton onClick={handleClose} />
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={!canSave()}
        >
          추가
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 

